// (c) chaehongee. Confidential and proprietary.

import { exportMultiLayerFrame, type LayerExportData } from '../components/Canvas/services/imageExport'
import { uploadNode } from './node'
import { createCommit } from './commit'
import type { NodeResponse } from 'models-ts/node'
import type { CommitResponse } from 'models-ts/commit'

export interface ExportFrameLayerParams {
    id: string
    sourceUrl: string
    imageRect: { x: number; y: number; w: number; h: number }
    frameRect: { x: number; y: number; w: number; h: number }
    imageNaturalSize: { w: number; h: number }
    initialImageRect?: { x: number; y: number; w: number; h: number }
    zIndex?: number
    overlayBlob?: Blob | null
}

export interface ExportFrameParams {
    sessionId: string
    frameRect: { x: number; y: number; w: number; h: number }
    layers: ExportFrameLayerParams[]
    commitMessage?: string
    baseNodeIds: string[]
}

export interface ExportFrameResult {
    node: NodeResponse
    commit: CommitResponse
}

/**
 * Exports a framed image with one or more layers, uploads it as a new node,
 * and creates a commit linking the base nodes to the new node.
 * 
 * This is the single source of truth for frame export operations,
 * used by both Canvas (confirm button) and Backstage (chat context).
 */
export async function exportAndUploadFrame(
    params: ExportFrameParams
): Promise<ExportFrameResult> {
    const { sessionId, frameRect, layers, commitMessage = 'Edited: frame resize', baseNodeIds } = params

    if (layers.length === 0) {
        throw new Error('No layers to export')
    }

    // Build layer export data
    const layersToExport: LayerExportData[] = layers.map((layer) => ({
        sourceUrl: layer.sourceUrl,
        imageRect: layer.imageRect,
        imageNaturalSize: layer.imageNaturalSize,
        initialImageRect: layer.initialImageRect,
        zIndex: layer.zIndex ?? 0,
        overlayBlob: layer.overlayBlob ?? null,
    }))

    // Export the frame
    const result = await exportMultiLayerFrame({
        frameRect,
        layers: layersToExport
    })

    if (!result) {
        throw new Error('Frame export failed')
    }

    // Upload as a new node
    const file = new File([result.blob], 'framed.png', { type: 'image/png' })
    const node = await uploadNode(file, sessionId, 'image')

    if (!node?.id) {
        throw new Error('Node upload failed')
    }

    // Create a commit linking base nodes to the new node
    const commit = await createCommit({
        message: commitMessage,
        type: 'edited',
        session_id: sessionId,
        node_to_id: node.id,
        nodes_from_ids: baseNodeIds,
    })

    return { node, commit }
}
